import React, { useState } from 'react';
import axios from '../utils/axiosConfig';
import { Button, CircularProgress, IconButton, Box } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

function PhotoUpload({
  e_id,
  token,
  fileCategory,
  tableName,
  columnName,
  idColumn,
  onUploadSuccess,
  onRemoveSuccess,
  onImageClick,
  buttonText = "選擇檔案",
  currentFileName,
  showImagePreview = true,
  allowReplace = false
}) {
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [removing, setRemoving] = useState(false);

  const fileInputRef = React.useRef();

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      setErrorMessage('檔案大小不能超過 10MB');
      return;
    }
    // 預覽圖片
    const reader = new FileReader();
    reader.onloadend = () => setPreviewUrl(reader.result);
    reader.readAsDataURL(file);

    const formData = new FormData();
    formData.append('photo', file);
    formData.append('e_id', e_id);
    formData.append('fileCategory', fileCategory);
    formData.append('tableName', tableName);
    formData.append('columnName', columnName);
    formData.append('idColumn', idColumn);

    try {
      setUploading(true);
      setErrorMessage('');
      const response = await axios.post('/upload-photo', formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });

      const { filePath } = response.data;
      const fileName = filePath.split('/').pop();
      onUploadSuccess(fileName);
      alert('海報上傳成功！');
    } catch (error) {
      setErrorMessage('海報上傳失敗，請稍後重試。');
    } finally {
      setUploading(false);
    }
  };

  const handleRemovePhoto = async () => {
    if (!currentFileName) return;
    
    if (!window.confirm('確定要移除這張圖片嗎？')) return;
    
    try {
      setRemoving(true);
      setErrorMessage('');
      
      // 發送移除請求到後端
      await axios.delete('/remove-photo', {
        headers: { Authorization: `Bearer ${token}` },
        data: {
          fileName: currentFileName,
          fileCategory: fileCategory,
          tableName: tableName,
          columnName: columnName,
          idColumn: idColumn,
          id: e_id
        }
      });
      
      // 清空預覽和通知父組件
      setPreviewUrl('');
      if (onRemoveSuccess) {
        onRemoveSuccess();
      }
      alert('圖片移除成功！');
    } catch (error) {
      setErrorMessage('移除圖片失敗，請稍後重試。');
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div>
      {/* 隱藏 input */}
      <input
        type="file"
        accept="image/*"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleFileChange}
        disabled={uploading || removing}
      />
      
      {/* 現有圖片顯示 */}
      {showImagePreview && currentFileName && !previewUrl && (
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <img
            src={`/uploads/${fileCategory}/${currentFileName}?t=${new Date().getTime()}`}
            alt="當前圖片"
            style={{ width: 100, height: 120, borderRadius: 8, cursor: onImageClick ? 'pointer' : 'default' }}
            onClick={() => onImageClick && onImageClick(`/uploads/${fileCategory}/${currentFileName}?t=${new Date().getTime()}`)}
          />
          <IconButton
            onClick={handleRemovePhoto}
            disabled={removing}
            style={{
              position: 'absolute',
              top: -5,
              right: -5,
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              padding: '2px',
              minWidth: 'auto',
              width: '20px',
              height: '20px'
            }}
            size="small"
          >
            <CloseIcon style={{ fontSize: '14px', color: 'red' }} />
          </IconButton>
        </div>
      )}
      
      {/* 預覽圖片 */}
      {showImagePreview && previewUrl && (
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <img 
            src={previewUrl} 
            alt="預覽" 
            style={{ width: 100, height: 120, borderRadius: 8, cursor: onImageClick ? 'pointer' : 'default' }} 
            onClick={() => onImageClick && onImageClick(previewUrl)}
          />
          <IconButton
            onClick={() => {
              setPreviewUrl('');
              fileInputRef.current.value = '';
            }}
            style={{
              position: 'absolute',
              top: -5,
              right: -5,
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              padding: '2px',
              minWidth: 'auto',
              width: '20px',
              height: '20px'
            }}
            size="small"
          >
            <CloseIcon style={{ fontSize: '14px', color: 'red' }} />
          </IconButton>
        </div>
      )}
      
      {/* 加載狀態 */}
      {(uploading || removing) && <CircularProgress size={24} />}
      
      {/* 只顯示控制按鈕（不顯示圖片）時，提供顯式移除按鈕 */}
      {!uploading && !removing && !showImagePreview && currentFileName && (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mt: 1 }}>
          <Button
            variant="outlined"
            color="error"
            onClick={handleRemovePhoto}
          >
            移除照片
          </Button>
        </Box>
      )}

      {/* 自訂按鈕觸發 input */}
      {!uploading && !removing && (!currentFileName || allowReplace) && (
        <Button
          variant="contained"
          color="primary"
          onClick={() => fileInputRef.current.click()}
          sx={{ mt: 1 }}
        >
          {buttonText}
        </Button>
      )}
      
      {/* 錯誤訊息 */}
      {errorMessage && <div style={{ color: 'red' }}>{errorMessage}</div>}
    </div>
  );
}

export default PhotoUpload;
